import React from "react";
import { JsonFormsDispatch, withJsonFormsArrayControlProps } from "@jsonforms/react";
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Autocomplete,
  TextField,
  Avatar,
  Collapse
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { rankWith, and, schemaMatches, uiTypeIs,findUISchema } from "@jsonforms/core";

const ArrayObjectPrimitiveEnumRendererUI = ({
  data,
  path,
  schema,
  handleChange
}) => {
  const items = Array.isArray(data) ? data : [];
  const properties = schema?.properties || {};
  const title = schema?.title ?? "Items";
  const [expanded, setExpanded] = React.useState({});
  // ---- mutators ----
  const addItem = () => {
    handleChange(path, [...items, {}]);
    setExpanded((prev) => ({ ...prev, [items.length]: false }));
  };

  const removeItem = (index) => {
    handleChange(
      path,
      items.filter((_, i) => i !== index)
    );
  };

  const moveItem = (from, to) => {
    if (to < 0 || to >= items.length) return;
    const arr = [...items];
    const moved = arr.splice(from, 1)[0];
    arr.splice(to, 0, moved);
    handleChange(path, arr);
  };

  const updateField = (itemIndex, key, newValue) => {
    const newItems = [...items];
    newItems[itemIndex] = { ...newItems[itemIndex], [key]: newValue };
    handleChange(path, newItems);
  };
  return (
    <Box sx={{ mb: 3 }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "flex-start",alignItems: "center", gap: 2, mb: 2 }}>
        <Typography variant="h6">{title}</Typography>
        <IconButton onClick={addItem}>
          <AddIcon />
        </IconButton>
      </Box>

      {/* All Items */}
      {items.map((item, index) => (
        <Paper key={index} sx={{ p: 0, mb: expanded[index] ? 2 : 0.1 , mt: expanded[index] && index !== 0 ? 2 : 0, borderRadius: 1, overflow: "hidden" }}>
          {/* ITEM HEADER */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2,
              py: 1.5,
              bgcolor: "#F0FBFF"
            }}
            onClick={() => setExpanded((prev) => ({ ...prev, [index]: !(prev[index] ?? false) }))}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: "#BDBDBD",
                  color: "white",
                  fontWeight: 400
                }}
              >
                {index + 1}
              </Avatar>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton disabled={index === 0} onClick={(e) =>{
                e.preventDefault();
                e.stopPropagation();
                moveItem(index, index - 1);
              }}>
                <ArrowUpwardIcon />
              </IconButton>
              <IconButton
                disabled={index === items.length - 1}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  moveItem(index, index + 1);
                }}
              >
                <ArrowDownwardIcon />
              </IconButton>
              <IconButton onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                removeItem(index);
              }}>
                <DeleteIcon />
              </IconButton>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  setExpanded((prev) => ({ ...prev, [index]: !(prev[index] ?? false) }));
                }}
              >
                {(expanded[index] ?? false) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
          </Box>

          {/* FORM FIELDS */}
          <Collapse in={expanded[index] ?? false} timeout="auto" unmountOnExit={false}>
            <Box sx={{ p: 2 }}>
              {Object.entries(properties).map(([key, propSchema]) => (
                <Box key={key} sx={{ mb: 2 }}>
                  <Typography sx={{ mb: 1, fontWeight: 500 }}>{key}</Typography>

                  <Autocomplete
                    options={Array.isArray(propSchema.enum) ? propSchema.enum : []}
                    value={item?.[key] ?? null}
                    getOptionLabel={(option) => String(option)}
                    isOptionEqualToValue={(option, value) => option === value}
                    onChange={(e, v) => {
                      if(v)
                     { updateField(index, key, v)}
                    }}
                    renderInput={(params) => (
                      <TextField {...params} size="small" placeholder={`Select ${key}`} />
                    )}
                  />
                </Box>
              ))}
            </Box>
          </Collapse>
        </Paper>
      ))}
    </Box>
  );
};

export default ArrayObjectPrimitiveEnumRendererUI;

export const ArrayObjectPrimitiveEnumRenderer = withJsonFormsArrayControlProps(
  ArrayObjectPrimitiveEnumRendererUI
);



export const arrayObjectPrimitiveEnumTester = rankWith(
  1001, // higher than Material Table
  and(
    ((() => {
      const base = uiTypeIs("VerticalLayout");
      return (uischema, schema, ctx) => {
        const res = base(uischema, schema, ctx);
        return res;
      };
    })()),
    ((() => {
      const predicate = (schema) => {
        if (!schema || schema.type !== "array") return false;
        if (!schema.items || schema.items.type !== "object") return false;

        const props = schema.items.properties || {};
        const keys = Object.keys(props);

        if (keys.length === 0) return false;

        return keys.every((k) => {
          const f = props[k];
          return (
            f &&
            ["string", "number", "integer", "boolean"].includes(f.type) &&
            Array.isArray(f.enum)
          );
        });
      };
      const base = schemaMatches(predicate);
      return (uischema, schema, ctx) => {
        const res = base(uischema, schema, ctx);
        return res;
      };
    })())
  )
);
